import React from "react";
import { useTranslation } from "react-i18next";
import { Reveal } from "@/components/Reveal";
import { AuroraBackground } from "@/components/AuroraBackground";
import { LocaleLink } from "@/components/LocaleLink";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { Star, Shield, Sparkles, Compass } from "lucide-react";

const MAX_TESTIMONIALS = 6;

/**
 * Social-proof section.
 *
 * Truthful by design: it ships a verified "trust" band today, and the real
 * testimonial cards + App Store rating line only render once you populate the
 * i18n keys with REAL content (see public/assets/lifestyle/README is for the
 * photo; testimonial keys live in locales/landing/*.json under `testimonials`
 * and `proof.rating`). We never fabricate quotes or ratings — fake reviews
 * violate FTC + App Store policy, and fake AggregateRating gets pages penalized.
 *
 * To go live with real proof:
 *   landing:testimonials.items.0.quote / .name / .role / .rating  (repeat 0..5)
 *   landing:proof.rating.value  (e.g. "4.8")   landing:proof.rating.count ("1,200")
 *   landing:proof.rating.source ("App Store")
 */
export const Proof = () => {
  const { t } = useTranslation(["landing", "site"]);

  const testimonials = [];
  for (let i = 0; i < MAX_TESTIMONIALS; i += 1) {
    const quote = t(`testimonials.items.${i}.quote`, { defaultValue: "" });
    if (!quote) continue;
    testimonials.push({
      quote,
      name: t(`testimonials.items.${i}.name`, { defaultValue: "" }),
      role: t(`testimonials.items.${i}.role`, { defaultValue: "" }),
      rating: Math.max(1, Math.min(5, Number(t(`testimonials.items.${i}.rating`, { defaultValue: "5" })) || 5)),
    });
  }
  const hasTestimonials = testimonials.length > 0;

  const ratingValue = t("proof.rating.value", { defaultValue: "" });
  const ratingCount = t("proof.rating.count", { defaultValue: "" });
  const ratingSource = t("proof.rating.source", { defaultValue: "App Store" });
  const hasRating = Boolean(ratingValue && ratingCount);

  const trust = [
    {
      icon: <Shield size={18} />,
      title: t("proof.trust.privacy.title", "Private by design"),
      body: t("proof.trust.privacy.body", "You decide what's shared and when. Your relationship data is never sold."),
    },
    {
      icon: <Sparkles size={18} />,
      title: t("proof.trust.free.title", "Free forever plan"),
      body: t("proof.trust.free.body", "Start building daily connection with no credit card required."),
    },
    {
      icon: <Compass size={18} />,
      title: t("proof.trust.seasons.title", "Built for real seasons"),
      body: t("proof.trust.seasons.body", "Use it with one partner, a couple, or as a private lens across chapters."),
    },
  ];

  return (
    <section className="relative py-24" id="proof">
      <AuroraBackground variant="soft" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="eyebrow">{t("proof.kicker", "Why people choose Aurora")}</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-3xl">
            {t("proof.title", "Built to earn trust, before it asks for any.")}
          </h2>
          {hasRating ? (
            <div className="mt-5 flex items-center gap-2 text-white/75" data-testid="proof-rating">
              <span className="flex gap-0.5 text-[var(--aurora-gold)]" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={16} fill="currentColor" stroke="none" />
                ))}
              </span>
              <span className="text-sm">
                <strong className="text-white">{ratingValue}</strong> · {ratingCount} {t("proof.rating.label", "ratings on the")} {ratingSource}
              </span>
            </div>
          ) : null}
        </Reveal>

        {hasTestimonials ? (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((tm, i) => (
              <Reveal key={`tm-${i}`} delay={(i % 3) * 80}>
                <figure className="aurora-glass p-6 h-full flex flex-col">
                  <div className="flex gap-1 text-[var(--aurora-gold)] mb-3" aria-hidden="true">
                    {Array.from({ length: tm.rating }).map((_, s) => (
                      <Star key={s} size={14} fill="currentColor" stroke="none" />
                    ))}
                  </div>
                  <blockquote className="text-white/85 leading-relaxed font-light flex-1">
                    &ldquo;{tm.quote}&rdquo;
                  </blockquote>
                  {tm.name || tm.role ? (
                    <figcaption className="mt-4 text-sm text-white/55">
                      {tm.name}
                      {tm.name && tm.role ? " · " : ""}
                      {tm.role}
                    </figcaption>
                  ) : null}
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            {trust.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 80}>
                <div className="aurora-glass p-6 h-full">
                  <span className="size-9 rounded-full bg-white/5 flex items-center justify-center text-[var(--aurora-gold)] mb-3">
                    {c.icon}
                  </span>
                  <p className="font-serif text-xl">{c.title}</p>
                  <p className="mt-2 text-white/65 text-[0.95rem] leading-relaxed font-light">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {/* Founder note — real provenance, links to the full story */}
        <Reveal delay={140}>
          <div className="mt-8 aurora-glass p-7 sm:p-8 max-w-2xl mx-auto text-center">
            <p className="font-serif text-xl sm:text-2xl italic text-white/85 leading-relaxed">
              &ldquo;{t("proof.founder.quote", "We built Aurora because we needed it ourselves — a quiet space, a coach with memory, and privacy that isn't a footnote.")}&rdquo;
            </p>
            <p className="mt-4 text-sm text-white/55">
              {t("proof.founder.attribution", "The couple behind withAurora.love")}
            </p>
            <LocaleLink
              to="/story"
              className="mt-3 inline-flex items-center gap-1 text-[var(--aurora-cyan)] text-sm hover:gap-2 transition-all"
            >
              {t("proof.founder.cta", "Read our story")}
            </LocaleLink>
          </div>
        </Reveal>

        <Reveal delay={160}>
          <div className="mt-10 flex flex-col items-center gap-4 text-center">
            <p className="text-white/60 text-sm max-w-md">
              {t("proof.ctaLine", "Join the early members building steadier, closer relationships — one small moment at a time.")}
            </p>
            <StoreBadgeGroup startLabel={t("proof.ctaButton", "Start Free Now")} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
